// Anatomia do modal de integração — mockups "Modal Integração Typebot / n8n /
// Dialogflow / SGP" (900px): seletor de tipo em 6 cards, campos à esquerda e
// painel explicativo à direita (#0E1119).
import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "../../styles/aerosTokens";

export const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const useIntegrationModalStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderSoft = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight
    ? theme.palette.aeros.borderStrong
    : "rgba(255,255,255,0.1)";
  const fieldBg = isLight ? theme.palette.aeros.surface2 : "#12151F";
  const sideBg = isLight ? theme.palette.aeros.surface2 : "#0E1119";

  return {
    head: {
      padding: "0 24px 16px",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      borderBottom: `1px solid ${borderSoft}`,
    },
    typeGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(6,minmax(0,1fr))",
      gap: 8,
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(3,minmax(0,1fr))",
      },
    },
    typeCard: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      padding: "12px 6px",
      borderRadius: 10,
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 11.5,
      fontWeight: 600,
      minWidth: 0,
      backgroundColor: fieldBg,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      color: theme.palette.text.secondary,
      "&:hover": { borderColor: alpha(theme.palette.primary.main, 0.4) },
      "&:focus-visible": { outline: `2px solid ${AEROS.cyan}`, outlineOffset: 1 },
    },

    split: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1.5fr) minmax(0,1fr)",
      minHeight: 0,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0,1fr)" },
    },
    splitSingle: { gridTemplateColumns: "minmax(0,1fr)" },
    left: {
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      minWidth: 0,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    right: {
      padding: "20px 24px",
      backgroundColor: sideBg,
      borderLeft: `1px solid ${borderSoft}`,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      minWidth: 0,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    panelTitle: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
    },
    panelCode: {
      backgroundColor: isLight ? theme.palette.aeros.surface : "#12151F",
      border: `1px solid ${borderSoft}`,
      borderRadius: 10,
      padding: "10px 12px",
      fontFamily: MONO,
      fontSize: 11.5,
      lineHeight: 1.6,
      color: theme.palette.text.secondary,
      margin: 0,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      maxHeight: 280,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    panelIntro: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      lineHeight: 1.5,
    },

    // ---- campos ----
    label: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: 6,
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
    required: { color: AEROS.error },
    labelHelper: { color: theme.palette.aeros.textTertiary, fontWeight: 500 },
    hint: { fontSize: 11, color: theme.palette.aeros.textTertiary, marginTop: 5 },
    pair: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
      gap: 12,
    },
    input: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: fieldBg,
        borderRadius: 10,
        fontSize: 13.5,
        color: theme.palette.text.primary,
        "& fieldset": { borderColor: borderInput },
        "&:hover fieldset": { borderColor: alpha(AEROS.cyan, 0.5) },
        "&.Mui-focused fieldset": { borderColor: AEROS.cyan, borderWidth: 1 },
        "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(AEROS.cyan, 0.15)}` },
        // Sem minHeight no multiline: o TextareaAutosize do MUI v4 mede por
        // um textarea sombra e um min-height ali estoura a altura do campo.
        "&.MuiOutlinedInput-multiline": { padding: 0 },
      },
      "& .MuiOutlinedInput-input": { padding: "9px 12px" },
      "& .MuiOutlinedInput-inputMultiline": {
        padding: "10px 12px",
        fontSize: 13,
        lineHeight: 1.55,
      },
      "& .MuiSelect-select": { padding: "9px 12px" },
    },
    inputMono: {
      "& .MuiOutlinedInput-input": { fontFamily: MONO, fontSize: 12.5 },
      "& .MuiOutlinedInput-inputMultiline": { fontFamily: MONO, fontSize: 12 },
    },

    // ---- bloco de teste ----
    testBlock: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      borderRadius: 12,
      padding: "10px 14px",
    },
    testTitle: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    testHint: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      lineHeight: 1.35,
    },
    testButton: {
      flex: "none",
      fontSize: 12,
      fontWeight: 600,
      textTransform: "none",
      color: stateText(theme, "#C08BFF", "purple"),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
      borderRadius: 8,
      padding: "6px 14px",
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 15 },
    },
    testResult: {
      display: "flex",
      alignItems: "flex-start",
      gap: 6,
      fontSize: 12,
      lineHeight: 1.45,
      borderRadius: 10,
      padding: "9px 12px",
      "& .MuiSvgIcon-root": { fontSize: 15, marginTop: 1, flex: "none" },
    },
    testOk: {
      color: stateText(theme, AEROS.success, "success"),
      backgroundColor: alpha(AEROS.success, 0.1),
    },
    testFail: {
      color: stateText(theme, AEROS.error, "error"),
      backgroundColor: alpha(AEROS.error, 0.1),
    },

    // ---- rodapé ----
    footer: {
      padding: "16px 24px",
      borderTop: `1px solid ${borderSoft}`,
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    },
    footerHint: { fontSize: 11.5, color: theme.palette.aeros.textTertiary },
    footerActions: { marginLeft: "auto", display: "flex", gap: 10 },
    cancelButton: {
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.16)"}`,
      color: theme.palette.text.primary,
      borderRadius: 10,
      padding: "8px 16px",
      fontWeight: 600,
      fontSize: 13,
      textTransform: "none",
    },
    saveButton: {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      color: "#07080D",
      borderRadius: 10,
      padding: "8px 18px",
      fontWeight: 700,
      fontSize: 13,
      textTransform: "none",
      boxShadow: "none",
      "&.Mui-disabled": { opacity: 0.5, color: "#07080D" },
    },
  };
});

export default useIntegrationModalStyles;
